/**
 * Persistence layer (SPEC §8).
 *
 * - Single localStorage key: "hoz.v1" (JSON).
 * - Write-through on every state change.
 * - Reads tolerate corrupt/missing data (fall back to empty state, never crash).
 * - Future schema changes bump `v` with an in-code migration; never silently drop data.
 *
 * The module is import-safe in environments without localStorage (e.g. SSR/tests):
 * every read/write is wrapped in try/catch. We also expose a `memoryStorage`
 * for tests via dependency injection in the createStorage() factory.
 */
import {
  STORAGE_KEY,
  DEFAULT_SETTINGS,
  type StorageState,
} from './types'

/** Minimal localStorage-like interface — lets tests inject an in-memory store. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function todayLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function yesterdayLocal(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function emptyState(): StorageState {
  return {
    v: 1,
    mastered: {},
    wrong: {},
    stats: {
      firstUse: todayLocal(),
      streak: { count: 0, lastDay: '' },
    },
    settings: { ...DEFAULT_SETTINGS },
  }
}

/**
 * Read and validate the persisted state. On any failure (missing key, invalid
 * JSON, wrong version that we don't know how to migrate, etc.) returns a fresh
 * empty state — never throws, never returns a half-broken state.
 */
export function loadState(ls: StorageLike): StorageState {
  let raw: string | null = null
  try {
    raw = ls.getItem(STORAGE_KEY)
  } catch {
    return emptyState()
  }
  if (!raw) return emptyState()
  try {
    const parsed: unknown = JSON.parse(raw)
    if (!isStorageState(parsed)) return emptyState()
    if (parsed.v !== 1) {
      // future: migrations table
      return emptyState()
    }
    // defensive defaults
    return {
      v: 1,
      mastered: parsed.mastered ?? {},
      wrong: parsed.wrong ?? {},
      stats: {
        firstUse: parsed.stats?.firstUse ?? todayLocal(),
        streak: {
          count: parsed.stats?.streak?.count ?? 0,
          lastDay: parsed.stats?.streak?.lastDay ?? '',
        },
      },
      settings: {
        sessionSize: (parsed.settings?.sessionSize ?? 10) as 10 | 25 | 50,
      },
    }
  } catch {
    return emptyState()
  }
}

function isStorageState(x: unknown): x is StorageState {
  if (typeof x !== 'object' || x === null) return false
  const o = x as Record<string, unknown>
  return (
    o.v === 1 &&
    typeof o.mastered === 'object' &&
    typeof o.wrong === 'object' &&
    typeof o.stats === 'object'
  )
}

export function saveState(ls: StorageLike, state: StorageState): void {
  try {
    ls.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // quota / private mode — fail silently, app still works without persistence
  }
}

// ── Mutations (pure) ───────────────────────────────────────────────────

export function markMastered(state: StorageState, id: string): StorageState {
  if (state.mastered[id]) return state
  return {
    ...state,
    mastered: { ...state.mastered, [id]: 1 },
    // wrong count is preserved per SPEC
  }
}

export function bumpWrong(state: StorageState, id: string): StorageState {
  const prev = state.wrong[id] ?? 0
  return {
    ...state,
    wrong: { ...state.wrong, [id]: prev + 1 },
  }
}

export function resetLevel(state: StorageState, level: number): StorageState {
  const prefix = `l${level}-`
  const mastered: Record<string, 1> = {}
  for (const [k, v] of Object.entries(state.mastered)) {
    if (!k.startsWith(prefix)) mastered[k] = v
  }
  const wrong: Record<string, number> = {}
  for (const [k, v] of Object.entries(state.wrong)) {
    if (!k.startsWith(prefix)) wrong[k] = v
  }
  return { ...state, mastered, wrong }
}

export function resetAll(state: StorageState): StorageState {
  // streak survives per SPEC §7.7
  return {
    ...emptyState(),
    stats: state.stats,
  }
}

export function setSessionSize(
  state: StorageState,
  size: 10 | 25 | 50,
): StorageState {
  return { ...state, settings: { sessionSize: size } }
}

// ── Streak ─────────────────────────────────────────────────────────────

/**
 * Bump the streak on the first correct answer of a local calendar day.
 *  - if lastDay === today  → no-op
 *  - if lastDay === yesterday → count+1
 *  - otherwise (older or empty) → count=1
 */
export function bumpStreak(state: StorageState): StorageState {
  const today = todayLocal()
  const last = state.stats.streak.lastDay
  let count: number
  if (last === today) {
    count = state.stats.streak.count
  } else if (last === yesterdayLocal()) {
    count = state.stats.streak.count + 1
  } else {
    count = 1
  }
  return {
    ...state,
    stats: { ...state.stats, streak: { count, lastDay: today } },
  }
}

// ── Factory ────────────────────────────────────────────────────────────

/** Convenience factory bound to window.localStorage. */
export function createStorage(ls: StorageLike = window.localStorage) {
  return {
    load: () => loadState(ls),
    save: (s: StorageState) => saveState(ls, s),
  }
}
